import React, { useEffect, useState } from 'react';
import { Table, Input, Breadcrumb, Button, message, Modal, DatePicker, Space, Grid } from 'antd';
import { Link } from 'react-router-dom';
import { DeleteOutlined, RightOutlined, FileExcelOutlined, FilePdfOutlined, DownloadOutlined, SearchOutlined } from '@ant-design/icons';
import { historiqueNumerosService } from '../../common/services';
import dayjs from 'dayjs';
import moment from 'moment';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import Can from '../../components/can';

const { RangePicker } = DatePicker;

const HistoriqueNumeros = () => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setLoading] = useState(false);

  // filtres serveur
  const [q, setQ] = useState('');
  const [dateRange, setDateRange] = useState(null); // [dayjs, dayjs]

  // pagination/tri serveur
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [sort, setSort] = useState('-dateChangement');

  // 🔹 Helpers responsive
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.sm;                 // < md
  const controlSize = isMobile ? 'small' : 'middle';
  const fullW = { width: isMobile ? '100%' : 'auto' }; // pleine largeur en mobile

  const fetchList = async () => {
    try {
      setLoading(true);
      const params = {
        page,
        limit,
        sort,
        q: q || undefined,
        start: dateRange?.[0]?.format('YYYY-MM-DD'),
        end: dateRange?.[1]?.format('YYYY-MM-DD'),
      };
      const data = await historiqueNumerosService.getList(params);

      if (Array.isArray(data)) {
        setItems(data);
        setTotal(data.length);
      } else {
        setItems(data.items || []);
        setTotal(data.total || 0);
      }
    } catch (e) {
      console.error('Erreur chargement historique numéros :', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit, sort, q, dateRange]);

  const formatPhoneNumber = (phone) => {
    if (!phone) return '';
    let local = phone.replace('+225', '').trim();
    return local.replace(/(\d{2})(?=\d)/g, '$1 ').trim();
  };

  // 🔹 Modal.confirm responsive
  const handleDelete = (row) => {
    const { _id, ancienNumero, nouveauNumero } = row;
    Modal.confirm({
      title: "Confirmer la suppression",
      content: (
        <>
          <p>Êtes-vous sûr de vouloir supprimer cet historique ?</p>
          <p><strong>Ancien n° :</strong> {formatPhoneNumber(ancienNumero)}</p>
          <p><strong>Nouveau n° :</strong> {formatPhoneNumber(nouveauNumero)}</p>
        </>
      ),
      okText: "Oui, supprimer",
      cancelText: "Annuler",
      okType: "danger",
      centered: true,
      width: isMobile ? 360 : 520,
      bodyStyle: { padding: isMobile ? 12 : 24 },
      okButtonProps: { danger: true, size: controlSize, style: fullW },
      cancelButtonProps: { size: controlSize, style: fullW },
      onOk: async () => {
        try {
          await historiqueNumerosService.supprimerHistoriqueNumerosById(_id);
          message.success("Historique numéro supprimé avec succès !");
          fetchList();
        } catch (error) {
          console.error("Erreur suppression :", error);
          message.error("Une erreur est survenue pendant la suppression.");
        }
      }
    });
  };

  // Export (prend toute la liste filtrée)
  const fetchAllForExport = async () => {
    const params = {
      page: 1,
      limit: 5000,
      sort,
      q: q || undefined,
      start: dateRange?.[0]?.format('YYYY-MM-DD'),
      end: dateRange?.[1]?.format('YYYY-MM-DD'),
    };
    const data = await historiqueNumerosService.getList(params);
    return Array.isArray(data) ? data : (data.items || []);
  };

  const exportExcel = async () => {
    try {
      const all = await fetchAllForExport();
      const rows = all.map(it => ({
        Nom: it?.user?.firstName || '',
        Prenom: it?.user?.lastName || '',
        AncienNumero: formatPhoneNumber(it.ancienNumero),
        NouveauNumero: formatPhoneNumber(it.nouveauNumero),
        DateChangement: it.dateChangementStr || moment(it.dateChangement).format('DD/MM/YYYY HH:mm'),
      }));
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Historique numeros');
      XLSX.writeFile(wb, 'historique_numeros.xlsx');
    } catch (e) {
      message.error('Export Excel: une erreur est survenue.');
    }
  };

  const exportCSV = async () => {
    try {
      const all = await fetchAllForExport();
      const rows = all.map(it => ({
        Nom: it?.user?.firstName || '',
        Prenom: it?.user?.lastName || '',
        AncienNumero: formatPhoneNumber(it.ancienNumero),
        NouveauNumero: formatPhoneNumber(it.nouveauNumero),
        DateChangement: it.dateChangementStr || moment(it.dateChangement).format('DD/MM/YYYY HH:mm'),
      }));
      const ws = XLSX.utils.json_to_sheet(rows);
      const csv = XLSX.utils.sheet_to_csv(ws);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      saveAs(blob, 'historique_numeros.csv');
    } catch (e) {
      message.error('Export CSV: une erreur est survenue.');
    }
  };

  const exportPDF = async () => {
    try {
      const all = await fetchAllForExport();
      const doc = new jsPDF();
      doc.text('Historique des changements de numéros', 14, 10);
      autoTable(doc, {
        startY: 20,
        head: [['Nom', 'Prénom', 'Ancien n°', 'Nouveau n°', 'Date']],
        body: all.map(it => [
          it?.user?.firstName || '',
          it?.user?.lastName || '',
          formatPhoneNumber(it.ancienNumero),
          formatPhoneNumber(it.nouveauNumero),
          it.dateChangementStr || moment(it.dateChangement).format('DD/MM/YYYY HH:mm')
        ]),
      });
      doc.save('historique_numeros.pdf');
    } catch (e) {
      message.error('Export PDF: une erreur est survenue.');
    }
  };

  const columns = [
    {
      title: 'Nom',
      dataIndex: ['user', 'firstName'],
      key: 'firstName',
      sorter: true,
      render: (v) => v || '',
    },
    {
      title: 'Prénom(s)',
      dataIndex: ['user', 'lastName'],
      key: 'lastName',
      sorter: true,
      render: (v) => v || '',
    },
    {
      title: 'Ancien numéro',
      dataIndex: 'ancienNumero',
      key: 'ancienNumero',
      sorter: true,
      render: (v) => formatPhoneNumber(v),
    },
    {
      title: 'Nouveau numéro',
      dataIndex: 'nouveauNumero',
      key: 'nouveauNumero',
      sorter: true,
      render: (v) => formatPhoneNumber(v),
    },
    {
      title: 'Date de changement',
      dataIndex: 'dateChangement',
      key: 'dateChangement',
      sorter: true,
      render: (d, row) => row.dateChangementStr || dayjs(d).format('DD/MM/YYYY HH:mm'),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <div style={{ display: 'flex', gap: 8 }}>
          <Can perm="userPhone.history:delete">
            <Button
              danger
              icon={<DeleteOutlined />}
              size="small"
              onClick={() => handleDelete(record)}
            />
          </Can>
        </div>
      ),
    },
  ];

  const onTableChange = (pagination, _filters, sorter) => {
    setPage(pagination.current || 1);
    setLimit(pagination.pageSize || 10);

    if (sorter?.field || sorter?.columnKey) {
      const key = sorter.columnKey || sorter.field;
      const dir = sorter.order === 'ascend' ? '' : '-';
      if (['firstName','lastName','ancienNumero','nouveauNumero','dateChangement'].includes(key)) {
        setSort(`${dir}${key}`);
      }
    }
  };

  return (
    <div className="content-wrapper">
      <div className="container-xxl flex-grow-1 container-p-y">
        <div className="row">
          <div className="col-md-12">

            <Breadcrumb
              separator={<RightOutlined style={{ fontSize: '16px', color: '#999', fontWeight: 'bolder' }} />}
              style={{ marginBottom: 16 }}
            >
              <Breadcrumb.Item>
                <Link to="/dashboard" style={{ fontSize: '16px', fontWeight: 'bolder' }}>🏠 ACCUEIL</Link>
              </Breadcrumb.Item>
              <Breadcrumb.Item>
                <span style={{ fontSize: '16px', fontWeight: 'bolder' }}>📞 HISTORIQUE MODIFICATION NUMÉROS</span>
              </Breadcrumb.Item>
            </Breadcrumb>

            <div className="card mb-4">
              <div className="card-body">
                <div className="d-flex justify-content-between mb-3 flex-wrap gap-2">
                  <Input
                    prefix={<SearchOutlined />}
                    placeholder="Rechercher (tous champs)"
                    value={q}
                    onChange={(e) => { setQ(e.target.value); setPage(1); }}
                    style={{ width: isMobile ? '100%' : 320 }}
                    size={controlSize}
                    allowClear
                  />

                  <RangePicker
                    onChange={(range) => { setDateRange(range); setPage(1); }}
                    placeholder={['Date début', 'Date fin']}
                    format="DD/MM/YYYY"
                    allowClear
                    size={controlSize}
                    style={{ width: isMobile ? '100%' : 'auto' }}
                  />
                </div>

                {/* 🔹 Export responsive */}
                <div style={{ marginBottom: 16 }}>
                  <Space wrap size={[8, 8]} style={{ width: '100%' }}>
                    <Button
                      icon={<FileExcelOutlined />}
                      onClick={exportExcel}
                      type="primary"
                      size={controlSize}
                      style={fullW}
                    >
                      Exporter Excel (.xlsx)
                    </Button>

                    <Button
                      icon={<DownloadOutlined />}
                      onClick={exportCSV}
                      type="default"
                      size={controlSize}
                      style={fullW}
                    >
                      Exporter CSV
                    </Button>

                    <Button
                      icon={<FilePdfOutlined />}
                      onClick={exportPDF}
                      danger
                      size={controlSize}
                      style={fullW}
                    >
                      Exporter PDF
                    </Button>
                  </Space>
                </div>

                <Table
                  rowKey="_id"
                  columns={columns}
                  dataSource={items}
                  loading={isLoading}
                  onChange={onTableChange}
                  size={controlSize}
                  scroll={{ x: true }}
                  pagination={{
                    current: page,
                    pageSize: limit,
                    total,
                    showSizeChanger: true,
                    pageSizeOptions: [5, 10, 20, 50, 100],
                    showLessItems: isMobile,
                  }}
                />
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default HistoriqueNumeros;
